import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
	PieChart,
	Pie,
	Cell,
	ResponsiveContainer,
	Legend,
	Tooltip,
} from 'recharts'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'

const COLORS = {
	promoters: 'rgb(34, 197, 94)', // green-500
	passives: 'rgb(51, 65, 85)', // slate-700
	detractors: 'rgb(239, 68, 68)', // red-500
}

interface WidgetNPSChartProps {
	widgetId: string
}

const WidgetNPSChart = ({ widgetId }: WidgetNPSChartProps) => {
	// First get the widget's team_id
	const { data: widgetData } = useQuery({
		queryKey: ['widget-team', widgetId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from('widgets')
				.select('team_id')
				.eq('id', widgetId)
				.single()
			
			if (error) throw error
			return data
		},
		enabled: !!widgetId,
	})
	
	const { data: npsDistribution, isLoading } = useQuery({
		queryKey: ['widget-nps-distribution', widgetId, widgetData?.team_id],
		queryFn: async () => {
			if (!widgetData?.team_id) throw new Error('No widget team ID found')

			const { data, error } = await supabase
				.from('responses')
				.select('score')
				.eq('widget_id', widgetId)
				.eq('team_id', widgetData.team_id)
				.not('score', 'is', null)

			if (error) {
				console.error('Error fetching NPS distribution:', error)
				throw error
			}

			const promoters = data.filter((r) => r.score >= 9).length
			const passives = data.filter((r) => r.score >= 7 && r.score <= 8).length
			const detractors = data.filter((r) => r.score <= 6).length

			const total = promoters + passives + detractors
			const npsScore =
				total > 0
					? Math.round((promoters / total) * 100 - (detractors / total) * 100)
					: 0

			return {
				data: [
					{ name: 'Promoters', value: promoters, color: COLORS.promoters },
					{ name: 'Passives', value: passives, color: COLORS.passives },
					{ name: 'Detractors', value: detractors, color: COLORS.detractors },
				],
				npsScore,
				total,
			}
		},
		enabled: !!widgetId && !!widgetData?.team_id,
	})

	if (isLoading) {
		return (
			<Card className="animate-fade-in">
				<CardHeader className="flex flex-row items-center justify-between">
					<CardTitle className="text-foreground">NPS Distribution</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-[300px]">
						<Skeleton className="h-full w-full" />
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card className="animate-fade-in">
			<CardHeader className="flex flex-row items-center justify-between">
				<CardTitle className="text-foreground">NPS Distribution</CardTitle>
				{npsDistribution?.npsScore !== undefined && (
					<div className="flex items-center gap-2 bg-muted px-3 py-1 rounded-full">
						<span className="text-sm text-muted-foreground">NPS Score:</span>
						<span
							className={cn(
								'font-bold',
								npsDistribution.npsScore >= 0
									? 'text-primary'
									: 'text-primary-dark'
							)}>
							{npsDistribution.npsScore}
						</span>
					</div>
				)}
			</CardHeader>
			<CardContent>
				<div className="h-[300px]">
					<ResponsiveContainer width="100%" height="100%">
						<PieChart>
							<Pie
								data={npsDistribution?.data || []}
								cx="50%"
								cy="50%"
								innerRadius={70}
								outerRadius={90}
								paddingAngle={4}
								dataKey="value">
								{(npsDistribution?.data || []).map((entry, index) => (
									<Cell
										key={`cell-${index}`}
										fill={entry.color}
										stroke={entry.color}
										strokeWidth={2}
									/>
								))}
							</Pie>
							<Tooltip
								content={({ active, payload }) => {
									if (active && payload && payload.length) {
										const data = payload[0].payload
										const percentage = (
											(data.value / (npsDistribution?.total || 1)) *
											100
										).toFixed(1)
										return (
											<div className="rounded-lg border bg-white p-2 shadow-sm">
												<div className="flex flex-col gap-1">
													<span className="text-sm font-medium text-foreground">
														{data.name}
													</span>
													<div className="flex items-center gap-2">
														<span
															className="text-2xl font-bold"
															style={{ color: data.color }}>
															{data.value}
														</span>
														<span className="text-sm text-muted-foreground">
															({percentage}%)
														</span>
													</div>
												</div>
											</div>
										)
									}
									return null
								}}
							/>
							<Legend
								verticalAlign="bottom"
								height={36}
								content={({ payload }) => (
									<div className="flex justify-center gap-6 pt-4">
										{payload?.map((entry: any) => (
											<div
												key={entry.value}
												className="flex items-center gap-2">
												<div
													className="h-3 w-3 rounded-full"
													style={{ backgroundColor: entry.color }}
												/>
												<span className="text-sm font-medium text-muted-foreground">
													{entry.value}
												</span>
											</div>
										))}
									</div>
								)}
							/>
						</PieChart>
					</ResponsiveContainer>
				</div>
			</CardContent>
		</Card>
	)
}

export default WidgetNPSChart
